import { useCallback, useMemo } from 'react';
import { Column, ColumnIdentity } from './useTablePages';
import useLocalStorageSettings from './useLocalStorageSettings';

export type OrderDirection = 'ASC' | 'DESC';

export type OrderBy = { col: string; dir: OrderDirection };

export const useOrderBy = (
  page: string,
  availableColumns?: Column[],
  defaultColumnId?: string,
  defaultDirection: OrderDirection = 'ASC'
) => {
  const defaultOrderBy = useMemo(
    () =>
      defaultColumnId ? [{ col: defaultColumnId, dir: defaultDirection }] : [],
    [defaultColumnId, defaultDirection]
  );

  const [stored, setOrderBy] = useLocalStorageSettings<OrderBy[]>(
    page,
    'orderBy'
  );

  const orderBy: OrderBy[] = useMemo(() => {
    if (stored && stored.length > 0) {
      if (availableColumns && availableColumns.length > 0) {
        const filtered = stored.filter((order) =>
          availableColumns.find(
            (column) =>
              column.sortable !== false &&
              (column.id !== undefined ? column.id : column.accessor) ===
                order.col
          )
        );
        if (filtered.length > 0) {
          return stored;
        }
      }
    }
    return defaultOrderBy;
  }, [defaultOrderBy, stored, availableColumns]);

  const orderByColumnId = orderBy.length > 0 ? orderBy[0].col : undefined;
  const orderByDirection = orderBy.length > 0 ? orderBy[0].dir : 'ASC';

  const onHeaderClick = useCallback(
    (column: ColumnIdentity) => {
      if (
        orderByColumnId === column.id &&
        orderByDirection === 'DESC' &&
        (column.id !== defaultColumnId || defaultDirection !== 'DESC')
      ) {
        setOrderBy([]);
        return;
      }

      let dir: OrderDirection = 'ASC';
      if (orderByColumnId === column.id && orderByDirection === 'ASC') {
        dir = 'DESC';
      }
      setOrderBy([
        {
          col: column.id !== undefined ? column.id : column.accessor,
          dir,
        },
      ]);
    },
    [
      orderByColumnId,
      orderByDirection,
      defaultColumnId,
      defaultDirection,
      setOrderBy,
    ]
  );

  return {
    orderBy,
    setOrderBy,
    orderByColumnId,
    orderByDirection,
    onHeaderClick,
  };
};
